import React from 'react';
import { DateTimeDisplay, InfoIconWithTooltip, Select } from '@ygtechnologies/bygr-frontend-component-library';
import {
    EmailPipelineStage,
    MessageLogAttachment,
    MessageSendAttempt,
    MessageStatus,
    MessageStatusFilter,
    ReceivedEmailLogItem,
} from '@/services/communicore';
import theme from './Communicore.module.scss';

export function formatProject(projectName: string | null, projectId: number) {
    if (!projectId) {
        return null;
    }

    return projectName ? `${projectName} (${projectId})` : `${projectId}`;
}

export function formatFileSize(bytes: number) {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface MessageStatusCellProps {
    status: MessageStatus;
    lastError: string | null;
}

export const MessageStatusCell: React.FC<MessageStatusCellProps> = ({ status, lastError }) => (
    <span>
        {status} {lastError && <InfoIconWithTooltip content={lastError} />}
    </span>
);

const statusFilterOptions: { value: MessageStatusFilter; label: string }[] = [
    { value: '', label: 'All statuses' },
    { value: 'Sent', label: 'Sent' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Failed', label: 'Failed' },
];

interface StatusFilterSelectProps {
    value: MessageStatusFilter;
    onChange(value: MessageStatusFilter): void;
}

export const StatusFilterSelect: React.FC<StatusFilterSelectProps> = ({ value, onChange }) => (
    <div className={theme.statusFilter}>
        <Select
            value={statusFilterOptions.find((o) => o.value === value)}
            options={statusFilterOptions}
            onChange={(v) => onChange((v as { value: MessageStatusFilter }).value)}
        />
    </div>
);

interface SendAttemptsTableProps {
    attempts: MessageSendAttempt[];
}

export const SendAttemptsTable: React.FC<SendAttemptsTableProps> = ({ attempts }) => {
    if (attempts.length === 0) {
        return <p>No send attempts yet.</p>;
    }

    return (
        <table className="table style-3">
            <thead>
                <tr>
                    <th>#</th>
                    <th>Time</th>
                    <th>Result</th>
                    <th>Error</th>
                </tr>
            </thead>
            <tbody>
                {attempts.map((attempt) => (
                    <tr key={attempt.attemptNumber}>
                        <td>{attempt.attemptNumber}</td>
                        <td>
                            <DateTimeDisplay value={attempt.time} />
                        </td>
                        <td>{attempt.result}</td>
                        <td>{attempt.error}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

interface AttachmentListProps {
    attachments: MessageLogAttachment[];
    getUrl(fileId: string): string;
}

export const AttachmentList: React.FC<AttachmentListProps> = ({ attachments, getUrl }) => {
    if (attachments.length === 0) {
        return <p>No attachments.</p>;
    }

    return (
        <ul className={theme.attachmentList}>
            {attachments.map((attachment) => (
                <li key={attachment.fileId}>
                    <a href={getUrl(attachment.fileId)} target="_blank" rel="noreferrer">
                        {attachment.fileName}
                    </a>{' '}
                    <span className={theme.attachmentMeta}>
                        ({attachment.contentType}, {formatFileSize(attachment.size)})
                    </span>
                </li>
            ))}
        </ul>
    );
};

interface DetailListProps {
    items: { label: string; value: React.ReactNode }[];
}

export const DetailList: React.FC<DetailListProps> = ({ items }) => (
    <dl className={theme.detailList}>
        {items
            .filter((item) => item.value !== null && item.value !== undefined && item.value !== '')
            .map((item) => (
                <React.Fragment key={item.label}>
                    <dt>{item.label}</dt>
                    <dd>{item.value}</dd>
                </React.Fragment>
            ))}
    </dl>
);

const pipelineStageLabels: Record<EmailPipelineStage, string> = {
    NotLicensed: 'Not analysed: project has no CaseEmail license',
    Pending: 'Waiting for AI analysis',
    Retrying: 'AI analysis failing, retrying',
    DeadLettered: 'AI analysis gave up (dead-lettered)',
    AddedToExistingCase: 'Added to existing inbox case',
    CaseCreated: 'New inbox case created',
    AnalysedNoCase: 'Analysed, but no inbox case',
};

/** One-line summary of where a received case email is in Ruben's import → analyze pipeline. */
export const PipelineStageCell: React.FC<{ email: ReceivedEmailLogItem }> = ({ email }) => {
    if (email.type !== 'Case') {
        return null;
    }
    if (email.pipelineError) {
        return (
            <span>
                Unknown <InfoIconWithTooltip content={email.pipelineError} />
            </span>
        );
    }
    if (!email.pipeline) {
        return <span>Not imported by Ruben yet</span>;
    }

    return (
        <span>
            {pipelineStageLabels[email.pipeline.stage] ?? email.pipeline.stage}{' '}
            {email.pipeline.lastFailureReason && <InfoIconWithTooltip content={email.pipeline.lastFailureReason} />}
        </span>
    );
};
